/** @format */
import React, { FunctionComponent, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { MarketContext, MarketLanguage } from '@toucan/client/global/market';
import {
    TRUSTPILOT_TEMPLATE_IDS,
    getCommonTrustpilotConfig,
    trustpilot,
} from '@toucan/client/global/trustpilot';

import { styles } from './trustpilot.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum TrustpilotTheme {
    LIGHT = 'light',
    DARK = 'dark',
}

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum TrustpilotDisplayType {
    LONG = 'long',
    COMPACT = 'compact',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface TrustpilotProps {
    /**
     * Width that is applied as a data-width attribute on the
     * Trustpilot widget.
     * @default '100%'
     */
    width?: string;
    height: string;
    'data-testid'?: string;
    className?: string;
    templateId: string;
    theme?: TrustpilotTheme;
    isLeftAligned?: boolean;
    displayType?: TrustpilotDisplayType;
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledTrustpilotProps = {
    $marketLanguage: MarketLanguage;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    $isLeftAligned: boolean;
    $templateId: string;
};

const StyledTrustpilot = styled.div`
    ${styles}
`;

/**
 * Trustpilot widget
 */
export const Trustpilot: FunctionComponent<TrustpilotProps> = ({
    'data-testid': dataTestid,
    templateId,
    theme = TrustpilotTheme.LIGHT,
    width,
    height,
    className,
    isLeftAligned = false,
    displayType = TrustpilotDisplayType.LONG,
}: TrustpilotProps) => {
    const ref = useRef(null);
    const { activeMarket } = useContext(MarketContext);

    function getWidth() {
        if (width) {
            return width;
        }

        if (templateId === TRUSTPILOT_TEMPLATE_IDS.microCombo) {
            return displayType === TrustpilotDisplayType.LONG
                ? '500px'
                : '240px';
        }

        return '100%';
    }

    const config = {
        ...getCommonTrustpilotConfig(activeMarket),
        templateId,
    };

    useEffect(() => {
        try {
            trustpilot.loadFromRef(ref);
        } catch (error) {
            console.error('Failed to load Trustpilot widget:', error);
        }
    }, [
        width,
        height,

        /**
         * Watch every prop in config so the widget is only re-loaded
         * after prop change and not when there is a new object which may have
         * the same prop values
         */
        config.businessUnitId,
        config.locale,
        config.tags,
        templateId,
        theme,
        config.website,
        displayType,
    ]);

    return (
        <StyledTrustpilot
            $isLeftAligned={isLeftAligned}
            $marketLanguage={activeMarket.language}
            $templateId={templateId}
            // eslint-disable-next-line @mpb-com/mpb/transient-props-last -- Legacy code: do not use as an example.
            ref={ref}
            data-locale={config.locale}
            data-template-id={templateId}
            data-businessunit-id={config.businessUnitId}
            data-style-height={height}
            data-style-width={getWidth()}
            data-theme={theme}
            data-tags={config.tags}
            data-schema-type="Organization"
            data-testid={dataTestid}
            className={className}
        >
            <a
                href={`${config.website}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                Trustpilot
            </a>
        </StyledTrustpilot>
    );
};
